import { RenderTarget } from './renderTarget';
import type { TextureFormat } from './texture';

/**
 * Document-sized render targets handed out for the duration of one composite.
 * A nested tree needs a couple of buffers per level, and allocating those per
 * frame would thrash GPU memory, so they are recycled instead.
 */
export class BufferPool {
  private readonly all: RenderTarget[] = [];
  private readonly free: RenderTarget[] = [];

  constructor(
    private readonly gl: WebGL2RenderingContext,
    private readonly format: TextureFormat,
    private width: number,
    private height: number,
  ) {}

  acquire(): RenderTarget {
    const recycled = this.free.pop();
    if (recycled) {
      recycled.resize(this.width, this.height);
      return recycled;
    }
    const created = new RenderTarget(this.gl, this.width, this.height, this.format, 'linear');
    this.all.push(created);
    return created;
  }

  release(target: RenderTarget): void {
    // Guard against a double release putting the same buffer in play twice.
    if (this.free.includes(target)) return;
    this.free.push(target);
  }

  /** Returns every outstanding buffer, used at the end of a frame. */
  releaseAll(): void {
    this.free.length = 0;
    this.free.push(...this.all);
  }

  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;
    for (const target of this.all) target.resize(width, height);
  }

  get bytes(): number {
    return this.all.reduce((total, target) => total + target.bytes, 0);
  }

  get size(): number {
    return this.all.length;
  }

  dispose(): void {
    for (const target of this.all) target.dispose();
    this.all.length = 0;
    this.free.length = 0;
  }
}
